import _extends from '../../../node_modules/@babel/runtime/helpers/extends.js';
import _defineProperty from '../../../node_modules/@babel/runtime/helpers/defineProperty.js';
import _slicedToArray from '../../../node_modules/@babel/runtime/helpers/slicedToArray.js';
import _objectWithoutProperties from '../../../node_modules/@babel/runtime/helpers/objectWithoutProperties.js';
import { useState, useEffect, useCallback, useMemo } from 'react';
import 'prop-types';
import { jsx } from '@emotion/core';
import Label from '../Label/Label.js';
import Input from './Input.js';
import Select from '../Select/Select.js';
import SelectOption from '../Select/SelectOption.js';
import Flag from '../Flag/Flag.js';
import { useFieldControlContext } from '../FieldControl/FieldControlContext.js';
import getPreferredValue from '../../utils/getPreferredValue.js';
import { blockNonDigits } from '../../utils/keyUtils.js';
import noop from '../../utils/noop.js';
import forms from '../../styles/forms.js';
import { srOnly } from '../../styles/accessibility/srOnly.js';
import { borderDark } from '../../styles/borders/borderDark.js';
import { dlsColorWarning } from '../../styles/colorsDls/dlsColorWarning.js';
import phoneStyles from '../../styles/phone.js';
import { useThemeContext } from '../ThemeProvider/ThemeContext.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
var getMaxLength = function getMaxLength(format) {
  return (format.match(/#/g) || []).length;
};

var formatPhoneNumber = function formatPhoneNumber(val, selected) {
  if (!val) {
    return '';
  }

  var entry = val.replace(/\D/g, '').split('');
  var format = selected.phoneFormat.split('');
  var str = ''; // TODO: tech debt, replace for-of syntax
  // eslint-disable-next-line no-restricted-syntax

  var _iterator = _createForOfIteratorHelper(format),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var element = _step.value;

      if (element === '#') {
        if (entry.length === 0) break;
        str += entry.shift();
      } else {
        str += element;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return str;
}; // eslint-disable-next-line complexity


var PhoneInput = function PhoneInput(_ref) {
  var className = _ref.className,
      selectId = _ref.selectId,
      selectLabel = _ref.selectLabel,
      inputId = _ref.inputId,
      locale = _ref.locale,
      country = _ref.country,
      callingCode = _ref.callingCode,
      phoneFormat = _ref.phoneFormat,
      options = _ref.options,
      status = _ref.status,
      value = _ref.value,
      showFlag = _ref.showFlag,
      showCallingCode = _ref.showCallingCode,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      theme = _ref.theme,
      disabled = _ref.disabled,
      otherProps = _objectWithoutProperties(_ref, ["className", "selectId", "selectLabel", "inputId", "locale", "country", "callingCode", "phoneFormat", "options", "status", "value", "showFlag", "showCallingCode", "onBlur", "onChange", "theme", "disabled"]);

  var fieldControlContext = useFieldControlContext();
  var themeTokens = useThemeContext();

  var _ref2 = fieldControlContext || {},
      contextStatus = _ref2.contextStatus;

  var inputStatus = getPreferredValue(contextStatus, status);
  var initial = {
    locale: locale,
    country: country,
    callingCode: callingCode,
    phoneFormat: phoneFormat
  };

  var _useState = useState(initial),
      _useState2 = _slicedToArray(_useState, 2),
      selected = _useState2[0],
      setSelected = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      focused = _useState4[0],
      setFocused = _useState4[1];

  var _useState5 = useState(function () {
    return value || '';
  }),
      _useState6 = _slicedToArray(_useState5, 2),
      phoneNumber = _useState6[0],
      setPhoneNumber = _useState6[1];

  var _useState7 = useState(function () {
    return getMaxLength(phoneFormat);
  }),
      _useState8 = _slicedToArray(_useState7, 2),
      maxLength = _useState8[0],
      setMaxLength = _useState8[1];

  useEffect(function () {
    var nextValue = value;

    if (nextValue !== undefined) {
      if (nextValue === null || Number.isNaN(nextValue)) {
        nextValue = '';
      }

      if (nextValue !== phoneNumber) {
        setPhoneNumber(nextValue.slice(0, maxLength));
      }
    }
  }, [value, phoneNumber, maxLength, setPhoneNumber]);
  useEffect(function () {
    if (Array.isArray(options)) {
      var nextSelected = options.find(function (option) {
        return option.locale === locale;
      });

      if (!nextSelected) {
        var _options = _slicedToArray(options, 1);

        nextSelected = _options[0];
      }

      setSelected(nextSelected);
    }
  }, [locale, options, setSelected]);
  var handleSelect = useCallback(function (event) {
    var newSelected = options[event.target.selectedIndex];
    setSelected(newSelected);
    setPhoneNumber('');
    onChange({
      target: {
        value: ''
      }
    }, newSelected);
  }, [setSelected, setPhoneNumber, onChange, options]);
  useEffect(function () {
    setMaxLength(getMaxLength(selected.phoneFormat));
  }, [selected]);

  var handleChange = function handleChange(event) {
    var newPhoneNumber = event.target.value.replace(/\D/g, '').slice(0, maxLength);
    setPhoneNumber(newPhoneNumber);
    onChange(_objectSpread(_objectSpread({}, event), {}, {
      target: _objectSpread(_objectSpread({}, event.target), {}, {
        value: newPhoneNumber
      })
    }), selected);
  };

  var handleFocus = function handleFocus(event) {
    setPhoneNumber(event.target.value.replace(/\D/g, ''));
    setFocused(true);
  };

  var handleBlur = function handleBlur(event) {
    setFocused(false);
    onBlur(event, selected);
  };

  var phoneContainerStyle = useMemo(function () {
    return _objectSpread(_objectSpread({}, phoneStyles.phoneField), theme);
  }, [theme]);
  var phoneInputStyle = useMemo(function () {
    var inputStyle = {};

    if (options || showFlag) {
      inputStyle = {
        display: 'inline-block',
        input: {
          borderBottomLeftRadius: 0,
          borderTopLeftRadius: 0
        }
      };
    }

    if (showCallingCode) {
      inputStyle = _objectSpread(_objectSpread({}, inputStyle), {}, {
        input: _objectSpread(_objectSpread({}, inputStyle.input), {}, {
          paddingLeft: "".concat((callingCode.toString().length * 8 + 28) / 16, "rem")
        })
      });
    }

    return inputStyle;
  }, [callingCode, options, showCallingCode, showFlag]);
  var phoneSideStyle = useMemo(function () {
    var focusStyle = themeTokens.primaryColor ? {
      borderColor: themeTokens.primaryColor
    } : _objectSpread(_objectSpread({}, forms.formControl['&:active']), forms.formControl['&:focus']);

    var sideStyle = _objectSpread(_objectSpread(_objectSpread({}, focused ? focusStyle : {}), phoneStyles.phoneField__phoneSide), phoneStyles.phoneField__flag);

    if (inputStatus === 'error') {
      sideStyle = _objectSpread(_objectSpread({}, sideStyle), {}, {
        borderColor: dlsColorWarning.color
      });
    }

    return sideStyle;
  }, [focused, inputStatus, themeTokens]);
  var flagStyle = useMemo(function () {
    return _objectSpread(_objectSpread({
      padding: '0.75rem 0.938rem'
    }, borderDark), {}, {
      span: phoneStyles.phoneField__flag_span
    }, phoneSideStyle);
  }, [phoneSideStyle]);
  var selectStyle = useMemo(function () {
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, phoneStyles.phoneField__countries__select), focused ? phoneStyles.phoneField_focus_all_children__select : {}), inputStatus === 'error' ? phoneStyles.phoneField_hasWarning_all_children__select : {}), {}, {
      select: _objectSpread(_objectSpread({}, phoneStyles.phoneField__countries__select_select), {}, {
        marginTop: 0
      })
    });
  }, [focused, inputStatus]);
  var countryList = useMemo(function () {
    var countries = [];

    if (options) {
      options.forEach(function (option) {
        countries.push(jsx(SelectOption, {
          key: option.locale,
          value: option.country
        }, "".concat(option.country, " +").concat(option.callingCode)));
      });
    }

    return countries;
  }, [options]);
  var formattedPhoneNumber = useMemo(function () {
    return focused ? phoneNumber : formatPhoneNumber(phoneNumber, selected);
  }, [focused, phoneNumber, selected]);
  var selectComponent = useMemo(function () {
    return jsx("div", {
      css: phoneStyles.phoneField__countries
    }, jsx(Label, {
      htmlFor: selectId,
      css: srOnly
    }, selectLabel), jsx(Select, {
      id: selectId,
      onChange: handleSelect,
      theme: selectStyle,
      value: selected.country,
      disabled: disabled
    }, countryList), jsx("span", {
      css: phoneStyles.phoneField__countries__prefix
    }, "+ ".concat(selected.callingCode)));
  }, [countryList, disabled, handleSelect, selected, selectId, selectLabel, selectStyle]);
  return jsx("div", {
    css: phoneContainerStyle,
    className: className
  }, options ? selectComponent : showFlag && jsx("div", {
    css: flagStyle
  }, jsx(Flag, {
    countryCode: initial.locale.split('-')[1],
    alt: country
  })), jsx(Input, _extends({
    id: inputId,
    type: "tel",
    value: formattedPhoneNumber,
    onKeyDown: blockNonDigits,
    onChange: handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    status: status,
    theme: phoneInputStyle,
    disabled: disabled // eslint-disable-next-line react/jsx-props-no-spreading

  }, otherProps), showCallingCode && jsx("span", {
    css: phoneStyles.phoneField__phone__prefix
  }, "+ ".concat(callingCode))));
};

PhoneInput.defaultProps = {
  className: '',
  disabled: false,
  selectId: '',
  inputId: '',
  selectLabel: '',
  locale: 'en-US',
  country: 'United States',
  callingCode: 1,
  phoneFormat: '### ### ####',
  options: undefined,
  status: 'default',
  value: undefined,
  showFlag: false,
  showCallingCode: false,
  onBlur: noop,
  onChange: noop,
  theme: undefined
};

export default PhoneInput;
export { getMaxLength };
